import { apiFetch } from './edgePayloads.js';
const FULL_LISTING_ENDPOINT_BASE = 'https://api.xn--omrdeskollen-vcb.nu/listings/active/full';

export const normalizeHomeId = (value) => {
    if (value == null) return '';
    const digits = String(value).replace(/[^\d]/g, '').trim();
    return digits || '';
};

const extractHomeIdFromPath = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    const match = normalized.match(/\/(?:annons|bostad|projekt)\/(\d+)(?:[/?#]|$)/i);
    return match?.[1] ? normalizeHomeId(match[1]) : '';
};

const normalizeListingUrl = (value) => {
    const normalized = String(value || '').trim();
    return normalized || '';
};

const normalizeMediaArray = (value, fallback = null) => {
    const source = Array.isArray(value)
        ? value
        : (fallback != null ? [fallback] : []);
    return source
        .map((entry) => String(entry || '').trim())
        .filter(Boolean);
};

const normalizeBoolean = (value, defaultValue = false) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === '1') return true;
        if (normalized === 'false' || normalized === '0') return false;
    }
    return defaultValue;
};

export const getListingHomeId = (listing) => {
    const direct = normalizeHomeId(
        listing?.homeId
        ?? '',
    );
    if (direct) return direct;

    const listingUrl = normalizeListingUrl(
        listing?.listingUrl
        ?? listing?.listing_url
        ?? '',
    );
    const fromPath = extractHomeIdFromPath(listingUrl);
    if (fromPath) return fromPath;

    const listingId = String(listing?.id || '').trim();
    const idFromPath = extractHomeIdFromPath(listingId);
    if (idFromPath) return idFromPath;

    const secondaryCandidates = [
        listing?.realtorUrl,
        listing?.realtor_url,
        listing?.url,
        listing?.link,
        listing?.href,
    ];
    for (let index = 0; index < secondaryCandidates.length; index += 1) {
        const candidate = secondaryCandidates[index];
        const fromCandidate = extractHomeIdFromPath(candidate);
        if (fromCandidate) return fromCandidate;
    }

    return '';
};

// What identifies a listing: its kind and number, e.g. "bostad/4207563" or "annons/6295774". The number alone
// doesn't: the source's home ids (/bostad/) and ad ids (/annons/) overlap, so a few numbers belong to two
// different listings. Falls back to the bare number for a listing without its path.
const LISTING_PATH_RE = /(?:^|\/)(annons|bostad)\/(\d+)(?:[/?#]|$)/i;

export const getListingKey = (listing) => {
    const candidates = [listing?.listingUrl, listing?.listing_url, listing?.id];
    for (let index = 0; index < candidates.length; index += 1) {
        const match = String(candidates[index] || '').trim().match(LISTING_PATH_RE);
        if (match) return `${match[1].toLowerCase()}/${match[2]}`;
    }
    return getListingHomeId(listing);
};

// Each listing has a shareable URL: the current view's URL with a ?bostad=<n> or ?annons=<n> query param
// (the kind comes from the listing key, so the right one of two listings that share a number opens). The
// query param keeps whichever view the sharer was on (/ listvy, /kartvy) and needs no extra route.
const SHARE_PARAM_KINDS = ['bostad', 'annons'];

export const getListingShareParam = (listing) => {
    const match = String(getListingKey(listing) || '').match(/^(annons|bostad)\/(\d+)$/i);
    if (match) return { name: match[1].toLowerCase(), value: match[2] };
    const digits = normalizeHomeId(getListingKey(listing));
    return digits ? { name: 'bostad', value: digits } : null;
};

// Reads a shared listing's key out of a URL query string, e.g. "?bostad=123" -> "bostad/123" (or '').
export const readListingKeyFromSearch = (search) => {
    try {
        const params = new URLSearchParams(search || '');
        for (let index = 0; index < SHARE_PARAM_KINDS.length; index += 1) {
            const kind = SHARE_PARAM_KINDS[index];
            const value = normalizeHomeId(params.get(kind));
            if (value) return `${kind}/${value}`;
        }
    } catch (error) {
        /* malformed query string: no shared listing */
    }
    return '';
};

// The path+query (relative to `baseHref`) for a listing's shareable URL, with the share param set and any
// stale one removed; returns the unchanged URL when the listing has no resolvable key.
export const listingShareUrl = (listing, baseHref) => {
    const href = baseHref || (typeof window !== 'undefined' ? window.location.href : '');
    try {
        const url = new URL(href);
        SHARE_PARAM_KINDS.forEach((kind) => url.searchParams.delete(kind));
        const param = getListingShareParam(listing);
        if (param) url.searchParams.set(param.name, param.value);
        return url.pathname + url.search + url.hash;
    } catch (error) {
        return null;
    }
};

const toTrimmedText = (value) => String(value || '').trim();

const normalizeListingPayload = (payload) => {
    const homeId = getListingHomeId(payload);
    return {
        ...payload,
        homeId,
        listingUrl: normalizeListingUrl(payload?.listingUrl ?? payload?.listing_url ?? ''),
        images: normalizeMediaArray(payload?.images, payload?.image),
        planritning: normalizeMediaArray(payload?.planritning, payload?.floorplan),
        realtorUrl: toTrimmedText(payload?.realtorUrl ?? payload?.realtor_url ?? ''),
        fullData: normalizeBoolean(payload?.fullData ?? payload?.full_data, false),
    };
};

export const mergeListingDetails = (baseListing, incomingListing) => {
    const normalizedBase = normalizeListingPayload(baseListing || {});
    const normalizedIncoming = normalizeListingPayload(incomingListing || {});
    const merged = {
        ...normalizedBase,
        ...normalizedIncoming,
    };

    merged.homeId = normalizedIncoming.homeId || normalizedBase.homeId || '';
    merged.listingUrl = normalizedIncoming.listingUrl || normalizedBase.listingUrl || '';
    merged.fullData = normalizeBoolean(
        normalizedIncoming.fullData,
        normalizeBoolean(normalizedBase.fullData, false),
    );
    merged.images = normalizedIncoming.images.length ? normalizedIncoming.images : normalizedBase.images;
    merged.planritning = normalizedIncoming.planritning.length ? normalizedIncoming.planritning : normalizedBase.planritning;
    merged.realtorUrl = normalizedIncoming.realtorUrl || normalizedBase.realtorUrl || '';
    // Fields beyond the basics (year built, floor, valuation, amenities, …) only come with the details.
    merged.extra = normalizedIncoming.extra || normalizedBase.extra;
    if (!merged.extra) delete merged.extra;

    // A listing opened from a shared link starts as a stub, so its position comes from the details'
    // "lat,lng" (the Till jobbet row and the trip map need it).
    const hasCoordinate = (value) => value !== null && value !== '' && Number.isFinite(Number(value));
    if (!hasCoordinate(merged.lat) || !hasCoordinate(merged.lng)) {
        const [lat, lng] = String(merged.latlong || '').split(',').map((part) => Number(part.trim()));
        if (Number.isFinite(lat) && Number.isFinite(lng) && String(merged.latlong).includes(',')) {
            merged.lat = lat;
            merged.lng = lng;
        }
    }

    if (normalizedIncoming.refreshed != null) {
        merged.refreshed = normalizeBoolean(normalizedIncoming.refreshed, false);
    } else if (normalizedBase.refreshed != null) {
        merged.refreshed = normalizeBoolean(normalizedBase.refreshed, false);
    } else {
        delete merged.refreshed;
    }

    return merged;
};

const toComparableText = (value) => String(value || '').trim();

const areStringArraysEqual = (left, right) => {
    const leftValues = Array.isArray(left) ? left : [];
    const rightValues = Array.isArray(right) ? right : [];
    if (leftValues.length !== rightValues.length) return false;
    for (let index = 0; index < leftValues.length; index += 1) {
        if (toComparableText(leftValues[index]) !== toComparableText(rightValues[index])) {
            return false;
        }
    }
    return true;
};

export const listingHasDetailDifferences = (currentListing, nextListing) => {
    const current = normalizeListingPayload(currentListing || {});
    const next = normalizeListingPayload(nextListing || {});
    if (current.homeId !== next.homeId) return true;
    if (current.listingUrl !== next.listingUrl) return true;
    if (current.realtorUrl !== next.realtorUrl) return true;
    if (normalizeBoolean(current.fullData, false) !== normalizeBoolean(next.fullData, false)) return true;
    if (normalizeBoolean(current.refreshed, false) !== normalizeBoolean(next.refreshed, false)) return true;
    if (toComparableText(current.address) !== toComparableText(next.address)) return true;
    if (toComparableText(current.title) !== toComparableText(next.title)) return true;
    if (toComparableText(current.broker) !== toComparableText(next.broker)) return true;
    if (toComparableText(current.brokerLogo) !== toComparableText(next.brokerLogo)) return true;
    if (toComparableText(current.price) !== toComparableText(next.price)) return true;
    if (toComparableText(current.sqmPrice) !== toComparableText(next.sqmPrice)) return true;
    if (toComparableText(current.rooms) !== toComparableText(next.rooms)) return true;
    if (toComparableText(current.area) !== toComparableText(next.area)) return true;
    if (toComparableText(current.days) !== toComparableText(next.days)) return true;
    if (!areStringArraysEqual(current.images, next.images)) return true;
    if (!areStringArraysEqual(current.planritning, next.planritning)) return true;
    if (JSON.stringify(current.extra || null) !== JSON.stringify(next.extra || null)) return true;
    return false;
};

export const mergeListingDetailsIfChanged = (baseListing, incomingListing) => {
    const merged = mergeListingDetails(baseListing, incomingListing);
    return listingHasDetailDifferences(baseListing, merged) ? merged : baseListing;
};

export const getFullListingErrorMessage = (payload, fallbackMessage = 'Could not load full listing details') => {
    if (payload && typeof payload === 'object') {
        const backendError = toTrimmedText(payload?.error);
        if (backendError) return backendError;
    }
    return fallbackMessage;
};

// listingKey: from getListingKey ("annons/123"), or a bare number (looked up by number alone).
export const fetchFullListingDetails = async (listingKey, { signal } = {}) => {
    const pathMatch = String(listingKey || '').trim().match(LISTING_PATH_RE);
    const normalizedId = pathMatch ? pathMatch[2] : normalizeHomeId(listingKey);
    if (!normalizedId) {
        throw new Error('Invalid listing id');
    }
    const endpointPath = pathMatch ? `${pathMatch[1].toLowerCase()}/${normalizedId}` : normalizedId;

    const request = async (path) => {
        const res = await apiFetch(`${FULL_LISTING_ENDPOINT_BASE}/${path}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal,
        });
        let body = null;
        try {
            body = await res.json();
        } catch (error) {
            body = null;
        }
        return [res, body];
    };

    let [response, payload] = await request(endpointPath);
    // A backend without the kind/number route answers 404 without a JSON body (a missing listing has one):
    // ask by number instead.
    if (pathMatch && response.status === 404 && payload == null) {
        [response, payload] = await request(normalizedId);
    }

    if (!response.ok) {
        const error = new Error(getFullListingErrorMessage(payload));
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    const normalizedPayload = normalizeListingPayload(payload && typeof payload === 'object' ? payload : {});
    if (!normalizedPayload.homeId) {
        normalizedPayload.homeId = normalizedId;
    }
    return normalizedPayload;
};
